import { useAppSelector } from "../../common/hooks"
import { selectUserDetails } from "../../redux/slices/auth"
import { RequireRole } from "../../common/validation"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants/navbar"
import { usePaymentSettings } from "./hooks/use-payment-settings"
import PaymentMethodsSection from "./components/payment-methods-section"
import BillingAddressSection from "./components/billing-address-section"
import BillingEmailSection from "./components/billing-email-section"
import PaymentMethodsSkeleton from "./components/payment-methods-skeleton"
import AddPaymentForm from "../../common/ui/payment-management/add-payment-form"
import { useState } from "react"
import { LinkBtn, PrimaryBtn } from "../../common/ui"
import { Plus } from "lucide-react"

const PaymentManagement = () => {
  const userDetails = useAppSelector(selectUserDetails)
  const orgId = userDetails?.org?.id

  const {
    paymentSettings,
    isLoading,
    error,
    loadPaymentSettings,
    handleRemovePaymentMethod,
    handleSetDefaultPaymentMethod
  } = usePaymentSettings(orgId)

  const [showAddCard, setShowAddCard] = useState(false)

  if (isLoading) {
    return (
      <RequireRole
        allowedRoles={ALLOWED_ROLES_BY_ROUTE["account/payment-management"]}
      >
        <div className="p-6">
          <h2 className="font-bold text-xl pt-2">Payment Methods</h2>
          <p className="pt-2">Manage your saved credit and debit cards</p>
          <PaymentMethodsSkeleton />
        </div>
      </RequireRole>
    )
  }

  if (error) {
    return (
      <RequireRole
        allowedRoles={ALLOWED_ROLES_BY_ROUTE["account/payment-management"]}
      >
        <div className="p-6">
          <h2 className="font-bold text-xl">Payment Methods</h2>
          <p>Manage your saved credit and debit cards</p>
          <div className="h-96 flex items-center justify-center">
            <p className="text-red-600 mb-4 pt-6">{error}</p>
          </div>

          {showAddCard && (
            <AddPaymentForm
              orgId={orgId!}
              onSuccess={() => {
                setShowAddCard(false)
                loadPaymentSettings()
              }}
              onCancel={() => setShowAddCard(false)}
            />
          )}
        </div>
      </RequireRole>
    )
  }

  if (!paymentSettings) {
    return (
      <RequireRole
        allowedRoles={ALLOWED_ROLES_BY_ROUTE["account/payment-management"]}
      >
        <div className="p-6">
          <div className="flex justify-between items-center">
            <h2 className="font-bold text-xl">Payment Methods</h2>
            <PrimaryBtn onClick={() => setShowAddCard(true)}>
              <Plus size={20} className="mr-1" /> Add Payment Method
            </PrimaryBtn>
          </div>
          <p>Manage your saved credit and debit cards</p>
          <div className="h-96 flex items-center justify-center text-gray-500 ">
            <p>
              No payment settings found.{" "}
              <LinkBtn
                text="base"
                paddingOff
                onClick={() => setShowAddCard(true)}
              >
                <span className="font-normal">Add a payment method</span>
              </LinkBtn>{" "}
              to get started.
            </p>
          </div>
          {showAddCard && (
            <AddPaymentForm
              orgId={orgId!}
              onSuccess={() => {
                setShowAddCard(false)
                loadPaymentSettings()
              }}
              onCancel={() => setShowAddCard(false)}
            />
          )}
        </div>
      </RequireRole>
    )
  }

  return (
    <RequireRole
      allowedRoles={ALLOWED_ROLES_BY_ROUTE["account/payment-management"]}
    >
      <div className="p-6 pb-12">
        <div className="flex justify-between items-center">
          <h2 className="font-bold text-xl">Payment Methods</h2>
          {!showAddCard && (
            <PrimaryBtn onClick={() => setShowAddCard(true)}>
              <Plus size={20} className="mr-1" /> Add Payment Method
            </PrimaryBtn>
          )}
        </div>
        <div className="mt-6 space-y-4">
          {showAddCard ? (
            <AddPaymentForm
              orgId={orgId!}
              onSuccess={() => {
                setShowAddCard(false)
                loadPaymentSettings()
              }}
              onCancel={() => setShowAddCard(false)}
            />
          ) : (
            <PaymentMethodsSection
              paymentMethods={paymentSettings.payment_methods}
              orgId={orgId!}
              onRemove={handleRemovePaymentMethod}
              onSetDefault={handleSetDefaultPaymentMethod}
              onUpdate={loadPaymentSettings}
            />
          )}

          <BillingAddressSection
            address={paymentSettings.billing_address}
            orgId={orgId!}
            onUpdate={loadPaymentSettings}
          />

          <BillingEmailSection email={paymentSettings.primary_email} />
        </div>
      </div>
    </RequireRole>
  )
}

export default PaymentManagement
